import Core          from '@/core/Core';
import Utils         from '@/core/utils/Utils';
import BaseEntity    from '@/core/misc/BaseEntity';
import DateRange     from '@/core/types/DateRange';
import DateSimple    from '@/core/types/DateSimple';
import NumericRange  from '@/core/types/NumericRange';
import Price         from '@/core/types/Price';
import Currency      from '@/domains/Currencies/Currency';

import Title_ru_RU_Class from '@/core/misc/decorators/Title_ru_RU_Class';

import type SupplyOffer   from '@/domains/SupplyOffers/SupplyOffer';
import SupplyOffersService from '@/domains/SupplyOffers/SupplyOffersService';
import Title_ru_RU from "@/core/misc/decorators/Title_ru_RU";
import TYPES from "@/core/services/Changelogs/types";

@Title_ru_RU_Class('Тариф')
class SupplyOfferTariff extends BaseEntity {

	/** Диапазон количества: от и до */
  @Title_ru_RU('Диапазон количества', TYPES.INT_RANGE)
  public qty: NumericRange;

	/** Цена в валюте тарифа */
  @Title_ru_RU('Цена', TYPES.PRICE)
  public price: Price;

	/** Валюта цены. Один товар может продаваться в разных валютах одновременно */
  @Title_ru_RU('Валюта', TYPES.STRING)
  public currencyCode: string;

	/** Период действия цены. Дата окончания необязательна */
  @Title_ru_RU('Период действия цены', TYPES.DATE_RANGE)
	public validityPeriod: DateRange = new DateRange();

	/** Позиция заказа, фактической ценой которой уточнён тариф */
	public orderPositionId: number | null = null;

	/** Заказ этой позиции — для перехода из тарифа */
	public orderId: number | null = null;

	// фронтовое поле
	public _tempId: string;

	static fromServer(data: any, tariff: SupplyOfferTariff = new SupplyOfferTariff()): SupplyOfferTariff {
		super.fromServer(data, tariff);

		tariff.qty             = Core.utils.hydrate(data.qty, NumericRange.fromServer)!;
		// в старых записях журнала изменений период лежит под прежним именем actualFor
		tariff.validityPeriod  = Core.utils.hydrate(data.validityPeriod ?? data.actualFor, DateRange.fromServer)!;
		tariff.price           = Core.utils.hydrate(data.price, Price.fromServer)!;
		tariff.currencyCode    = data.currencyCode;
		tariff.orderPositionId = data.orderPositionId ?? null;
		tariff.orderId         = data.orderId ?? null;

		return tariff;
	}

	/**
	 * Создаёт пустой тариф.
	 * Левая граница qty берётся из конца предыдущего тарифа + 1.
	 * Валюта — как у предыдущего тарифа, без него — как у любого тарифа разрешения, иначе рубли.
	 */
	static getEmpty(supplyOffer: SupplyOffer, previousTariff?: SupplyOfferTariff | null): SupplyOfferTariff {
		const result = new this();

		result._tempId = crypto.randomUUID();

		let leftEdgeQty;

		if (previousTariff) {
			leftEdgeQty = (Utils.number.toNumber(previousTariff.qty?.end) ?? 0) + 1;
		}
		else {
			leftEdgeQty = undefined;
		}

		result.qty = new NumericRange(leftEdgeQty, undefined);

		result.validityPeriod.start = DateSimple.today();

		result.currencyCode = previousTariff?.currencyCode ?? supplyOffer.tariffs[0]?.currencyCode ?? Currency.initRubDefault().code;

		return result;
	}

	get currency(): Currency | null {
		return Core.utils.money.getCurrencyByCode(this.currencyCode);
	}

	set currency(currency: Currency) {
		this.currencyCode = currency.code;
	}

	get isExpired() {
		return Core.utils.date.dateDifference(this.validityPeriod.start, DateSimple.today()) > SupplyOffersService.PRICE_EXPIRATION_DAYS;
	}

	/** Цена ещё не действует: дата начала в будущем */
	get isFuture(): boolean {
		return this.validityPeriod.start?.isAfter(DateSimple.today()) === true;
	}

	/** Срок действия цены, указанный поставщиком, прошёл */
	get isFinished(): boolean {
		return this.validityPeriod.end?.isBefore(DateSimple.today()) === true;
	}

	/** Тариф уточнён фактической ценой из заказа */
	get isFromOrder(): boolean {
		return this.orderPositionId !== null;
	}

	/** Исправленная цена из заказа - уже не факт сделки, а обычный тариф: сохраняется новой записью без ссылки на заказ */
	detachFromOrder(): void {
		this.resetIdentity();
		this.orderPositionId = null;
		this.orderId         = null;
	}

	/** Сравнение с бюджетной ценой, уже пересчитанной в валюту тарифа */
	isLessOrEqualBudgetPrice(budgetPriceInTariffCurrency: Price): boolean {
		return this.price.lessThan(budgetPriceInTariffCurrency) || this.price.equals(budgetPriceInTariffCurrency);
	}

  isSameTariff(other: SupplyOfferTariff): boolean {
    const isEqualQty = this.qty?.equals(other.qty);
    const isEqualPrice = this.price?.equals(other.price);
    const isEqualCurrency = this.currencyCode === other.currencyCode;
    const isEqualValidityPeriod = this.validityPeriod.equals(other.validityPeriod);


    return isEqualQty && isEqualPrice && isEqualCurrency && isEqualValidityPeriod;
  }
}

export default SupplyOfferTariff;
